import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';

const SUPERADMIN_PWD_DEFAULT = 'Heslo123';

const EMPTY_USER_FORM = { name: '', email: '', username: '', password: '' };

function SuperAdminConsole({ onLogout }) {
  const [tenants, setTenants] = useState([]);
  const [migrationStats, setMigrationStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newTenant, setNewTenant] = useState({ name: '', slug: '', contactEmail: '' });
  const [creating, setCreating] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedTenantForMigration, setSelectedTenantForMigration] = useState('');

  // Uživatelský panel
  const [expandedTenant, setExpandedTenant] = useState(null); // id tenanta s otevřeným panelem
  const [tenantUsers, setTenantUsers] = useState({}); // { tenantId: [users] }
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userForm, setUserForm] = useState(EMPTY_USER_FORM);
  const [creatingUser, setCreatingUser] = useState(false);
  const [userMessage, setUserMessage] = useState('');
  const [syncingTenant, setSyncingTenant] = useState(null); // id tenanta právě synchronizovaného
  const [showUnassignedCompanies, setShowUnassignedCompanies] = useState(false);
  const [unassignedCompanies, setUnassignedCompanies] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);
  const [uploadingLogoFor, setUploadingLogoFor] = useState(null); // tenantId
  const [changingPasswordFor, setChangingPasswordFor] = useState(null); // userId
  const [newPasswordValue, setNewPasswordValue] = useState('');
  const [openActionMenu, setOpenActionMenu] = useState(null); // tenantId with open dropdown
  const [renamingTenant, setRenamingTenant] = useState(null); // tenantId being renamed
  const [renameValue, setRenameValue] = useState('');
  const actionMenuRef = useRef(null);
  const [consolePwd, setConsolePwd] = useState(SUPERADMIN_PWD_DEFAULT);
  const [pwdForm, setPwdForm] = useState({ current: '', newPwd: '', confirm: '' });
  const [pwdMessage, setPwdMessage] = useState('');

  // Supplier management state
  const [suppliers, setSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [supplierMessage, setSupplierMessage] = useState('');
  const [approveForm, setApproveForm] = useState({}); // { [id]: { tenantId, batchName } }
  const [resetPwdForm, setResetPwdForm] = useState({}); // { [id]: newPassword }
  const [showSupplierSection, setShowSupplierSection] = useState(false);

  // Request log state
  const [showRequestLog, setShowRequestLog] = useState(false);
  const [requestLog, setRequestLog] = useState(null);
  const [loadingRequestLog, setLoadingRequestLog] = useState(false);
  const [blockingUser, setBlockingUser] = useState(null); // userId právě blokovaného

  const authHeaders = {
    'Content-Type': 'application/json',
    'x-superadmin-key': consolePwd
  };

  // Close action dropdown when clicking outside
  useEffect(() => {
    if (!openActionMenu) return;
    const handler = (e) => {
      if (actionMenuRef.current && !actionMenuRef.current.contains(e.target)) {
        setOpenActionMenu(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openActionMenu]);

  const fetchTenants = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants`, { headers: authHeaders });
      if (!res.ok) throw new Error('Chyba načítání');
      const data = await res.json();
      setTenants(data);
    } catch (err) {
      setError('Nepodařilo se načíst tenenty: ' + err.message);
    }
  }, []);

  const fetchMigrationStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/superadmin/migration-stats`, { headers: authHeaders });
      if (!res.ok) throw new Error('Chyba načítání');
      const data = await res.json();
      setMigrationStats(data);
      if (data.tenants && data.tenants.length > 0 && !selectedTenantForMigration) {
        setSelectedTenantForMigration(data.tenants[0]._id);
      }
    } catch (err) {
      setError('Nepodařilo se načíst statistiky: ' + err.message);
    }
  }, [selectedTenantForMigration]);

  const fetchTenantUsers = useCallback(async (tenantId) => {
    setLoadingUsers(true);
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenantId}/users`, { headers: authHeaders });
      if (!res.ok) throw new Error('Chyba načítání');
      const data = await res.json();
      setTenantUsers(prev => ({ ...prev, [tenantId]: data }));
    } catch (err) {
      setUserMessage('Chyba načítání uživatelů: ' + err.message);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchTenants(), fetchMigrationStats()]);
      setLoading(false);
    };
    load();
  }, []);

  const handleToggleUsers = async (tenant) => {
    if (expandedTenant === tenant._id) {
      setExpandedTenant(null);
      setUserForm(EMPTY_USER_FORM);
      setUserMessage('');
      return;
    }
    setExpandedTenant(tenant._id);
    setUserForm(EMPTY_USER_FORM);
    setUserMessage('');
    await fetchTenantUsers(tenant._id);
  };

  const handleCreateUser = async (e, tenantId) => {
    e.preventDefault();
    if (!userForm.name || !userForm.password || (!userForm.email && !userForm.username)) {
      setUserMessage('Jméno, heslo a email nebo uživatelské jméno jsou povinné');
      return;
    }
    setCreatingUser(true);
    setUserMessage('');
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenantId}/users`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(userForm)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba vytváření');
      setUserMessage(`Uživatel "${data.name}" byl vytvořen jako admin.`);
      setUserForm(EMPTY_USER_FORM);
      await fetchTenantUsers(tenantId);
      await fetchTenants();
    } catch (err) {
      setUserMessage('Chyba: ' + err.message);
    } finally {
      setCreatingUser(false);
    }
  };

  const handleChangePassword = async (user) => {
    const trimmed = newPasswordValue.trim();
    if (!trimmed) return;
    if (trimmed.length < 4) { setUserMessage('Chyba: Heslo musí mít alespoň 4 znaky'); return; }
    try {
      const res = await fetch(`${API_URL}/users/${user._id}/password`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ password: trimmed })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Chyba změny hesla');
      setUserMessage(`Heslo uživatele "${user.name}" bylo změněno.`);
      setChangingPasswordFor(null);
      setNewPasswordValue('');
    } catch (err) {
      setUserMessage('Chyba: ' + err.message);
    }
  };

  const handleDeleteUser = async (tenantId, user) => {
    if (!window.confirm(`Smazat uživatele "${user.name}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenantId}/users/${user._id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Chyba mazání');
      setUserMessage(`Uživatel "${user.name}" byl smazán.`);
      await fetchTenantUsers(tenantId);
      await fetchTenants();
    } catch (err) {
      setUserMessage('Chyba: ' + err.message);
    }
  };

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (!newTenant.name || !newTenant.slug) {
      setMessage('Název a slug jsou povinné');
      return;
    }
    setCreating(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newTenant)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba vytváření');
      setMessage(`Partner "${data.name}" byl úspěšně vytvořen!`);
      setNewTenant({ name: '', slug: '', contactEmail: '' });
      await Promise.all([fetchTenants(), fetchMigrationStats()]);
    } catch (err) {
      setMessage('Chyba: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const handleMigrate = async () => {
    if (!selectedTenantForMigration) {
      setMessage('Nejprve vyberte tenanta pro migraci');
      return;
    }
    const tenant = tenants.find(t => t._id === selectedTenantForMigration);
    if (!window.confirm(`Přiřadit všechna nepřiřazená data tenantovi "${tenant?.name}"? Tato akce ovlivní ${migrationStats?.unassignedUsers || 0} uživatelů a ${migrationStats?.unassignedCompanies || 0} firem.`)) return;
    setMigrating(true);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/superadmin/migrate`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ tenantId: selectedTenantForMigration })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba migrace');
      setMessage(`Migrace dokončena! Aktualizováno: ${data.usersUpdated} uživatelů, ${data.companiesUpdated} firem.`);
      await Promise.all([fetchTenants(), fetchMigrationStats()]);
    } catch (err) {
      setMessage('Chyba migrace: ' + err.message);
    } finally {
      setMigrating(false);
    }
  };

  const handleSyncData = async (tenant) => {
    if (!window.confirm(`Synchronizovat data partnera "${tenant.name}"?\n\nPřiřadí tenantId firmám, stavům, AI konfiguraci, Call Centru, grafům, znalostem a denním zprávám.`)) return;
    setSyncingTenant(tenant._id);
    setMessage('');
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenant._id}/sync-data`, {
        method: 'POST',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba synchronizace');
      const parts = [
        `👤 ${data.usersFixed ?? 0} uživatelů`,
        `🏢 ${data.companiesFixed ?? 0} firem`,
        `📊 ${data.statusesByUserFixed ?? 0} stavů přiřazeno, ${data.statusesCloned ?? 0} výchozích zkopírováno`,
        `🤖 AI data: ${data.aiDataCloned ? 'zkopírováno' : 'již existuje'}`,
        `💬 AI prompt: ${data.aiPromptCloned ? 'zkopírováno' : 'již existuje'}`,
        `🛒 AI nabídka: ${data.aiOfferConfigCloned ? 'zkopírováno' : 'již existuje'}`,
        `📞 Call centrum: ${data.callCenterConfigCloned ? 'zkopírováno' : 'již existuje'}`,
        `📈 Nastavení grafů: ${data.chartSettingsCloned ? 'zkopírováno' : 'již existuje'}`,
        `🧠 Znalosti: ${data.knowledgeCloned ? `${data.knowledgeCloned} položek zkopírováno` : 'již existuje'}`,
        `📅 Denní zprávy: ${data.dailyMessagesCloned ? `${data.dailyMessagesCloned} zkopírováno` : 'již existuje'}`,
        `⚡ Šablony aktivit: ${data.activityTemplatesCloned ? `${data.activityTemplatesCloned} zkopírováno` : 'již existuje'}`,
        `📄 Šablony požadavků: ${data.requestTemplatesCloned ? `${data.requestTemplatesCloned} zkopírováno` : 'již existuje'}`,
      ];
      setMessage(`✅ Synchronizace "${tenant.name}" dokončena:\n${parts.join(' | ')}`);
      await Promise.all([fetchTenants(), fetchMigrationStats()]);
    } catch (err) {
      setMessage('Chyba synchronizace: ' + err.message);
    } finally {
      setSyncingTenant(null);
    }
  };

  const handleToggleUnassignedCompanies = async () => {
    if (showUnassignedCompanies) {
      setShowUnassignedCompanies(false);
      return;
    }
    setLoadingUnassigned(true);
    try {
      const res = await fetch(`${API_URL}/superadmin/unassigned-companies`, { headers: authHeaders });
      const data = await res.json();
      setUnassignedCompanies(Array.isArray(data) ? data : []);
      setShowUnassignedCompanies(true);
    } catch (err) {
      setUnassignedCompanies([]);
      setShowUnassignedCompanies(true);
    } finally {
      setLoadingUnassigned(false);
    }
  };

  const handleLogoUpload = async (tenant, file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { alert('Logo je příliš velké. Maximum je 2 MB.'); return; }
    setUploadingLogoFor(tenant._id);
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenant._id}/logo`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ logo: base64 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba nahrání');
      setMessage(`Logo partnera "${tenant.name}" bylo úspěšně uloženo.`);
      await fetchTenants();
    } catch (err) {
      setMessage('Chyba nahrání loga: ' + err.message);
    } finally {
      setUploadingLogoFor(null);
    }
  };

  const handleChangeConsolePassword = async (e) => {
    e.preventDefault();
    setPwdMessage('');
    if (pwdForm.current !== consolePwd) { setPwdMessage('Chyba: Současné heslo je nesprávné'); return; }
    if (pwdForm.newPwd.length < 6) { setPwdMessage('Chyba: Nové heslo musí mít alespoň 6 znaků'); return; }
    if (pwdForm.newPwd !== pwdForm.confirm) { setPwdMessage('Chyba: Nové heslo a potvrzení se neshodují'); return; }
    try {
      const res = await fetch(`${API_URL}/superadmin/change-password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-superadmin-key': consolePwd },
        body: JSON.stringify({ newPassword: pwdForm.newPwd })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba změny hesla');
      setConsolePwd(pwdForm.newPwd);
      setPwdForm({ current: '', newPwd: '', confirm: '' });
      setPwdMessage('Heslo bylo úspěšně změněno.');
    } catch (err) {
      setPwdMessage('Chyba: ' + err.message);
    }
  };

  const handleRenameTenant = async (tenant) => {
    const trimmed = renameValue.trim();
    if (!trimmed) return;
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenant._id}`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ name: trimmed })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba přejmenování');
      setMessage(`Partner byl přejmenován na "${data.name}".`);
      setRenamingTenant(null);
      setRenameValue('');
      await fetchTenants();
    } catch (err) {
      setMessage('Chyba: ' + err.message);
    }
  };

  const handleDeleteTenant = async (tenant) => {
    if (!window.confirm(`Opravdu smazat partnera "${tenant.name}"? Data zůstanou v databázi, pouze se odpojí od tohoto partnera.`)) return;
    try {
      const res = await fetch(`${API_URL}/superadmin/tenants/${tenant._id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      if (!res.ok) throw new Error('Chyba mazání');
      setMessage(`Partner "${tenant.name}" byl smazán.`);
      if (expandedTenant === tenant._id) setExpandedTenant(null);
      await Promise.all([fetchTenants(), fetchMigrationStats()]);
    } catch (err) {
      setMessage('Chyba: ' + err.message);
    }
  };

  // ===== Supplier handlers =====
  const fetchSuppliers = async () => {
    setLoadingSuppliers(true);
    try {
      const res = await fetch(`${API_URL}/superadmin/suppliers`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba načítání');
      setSuppliers(data.suppliers || []);
    } catch (err) {
      setSupplierMessage('Chyba: ' + err.message);
    } finally {
      setLoadingSuppliers(false);
    }
  };

  const fetchRequestLog = async () => {
    setLoadingRequestLog(true);
    try {
      const res = await fetch(`${API_URL}/superadmin/request-log`, { headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba načítání');
      setRequestLog(data);
    } catch (err) {
      setRequestLog({ error: err.message, stats: [] });
    } finally {
      setLoadingRequestLog(false);
    }
  };

  const blockUser = async (userId, minutes) => {
    setBlockingUser(userId);
    try {
      await fetch(`${API_URL}/superadmin/block-user`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ userId, minutes }),
      });
      await fetchRequestLog();
    } finally {
      setBlockingUser(null);
    }
  };

  const unblockUser = async (userId) => {
    setBlockingUser(userId);
    try {
      await fetch(`${API_URL}/superadmin/block-user/${userId}`, { method: 'DELETE', headers: authHeaders });
      await fetchRequestLog();
    } finally {
      setBlockingUser(null);
    }
  };

  const handleApproveSupplier = async (supplier) => {
    const form = approveForm[supplier._id] || {};
    if (!form.batchName) {
      setSupplierMessage('Zadejte název šarže před schválením.');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/superadmin/suppliers/${supplier._id}/approve`, {
        method: 'PUT',
        headers: authHeaders,
        body: JSON.stringify({ tenantId: form.tenantId || null, batchName: form.batchName })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba schválení');
      setSupplierMessage(`Dodavatel "${supplier.companyName}" byl schválen.`);
      fetchSuppliers();
    } catch (err) {
      setSupplierMessage('Chyba: ' + err.message);
    }
  };

  const handleRejectSupplier = async (supplier) => {
    if (!window.confirm(`Zamítnout dodavatele "${supplier.companyName}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/superadmin/suppliers/${supplier._id}/reject`, {
        method: 'PUT',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba zamítnutí');
      setSupplierMessage(`Dodavatel "${supplier.companyName}" byl zamítnut.`);
      fetchSuppliers();
    } catch (err) {
      setSupplierMessage('Chyba: ' + err.message);
    }
  };

  const handleDeleteSupplier = async (supplier) => {
    if (!window.confirm(`Opravdu smazat dodavatele "${supplier.companyName}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/superadmin/suppliers/${supplier._id}`, {
        method: 'DELETE',
        headers: authHeaders
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba mazání');
      setSupplierMessage(`Dodavatel "${supplier.companyName}" byl smazán.`);
      fetchSuppliers();
    } catch (err) {
      setSupplierMessage('Chyba: ' + err.message);
    }
  };

  const handleResetSupplierPassword = async (supplier) => {
    const newPwd = resetPwdForm[supplier._id];
    if (!newPwd || newPwd.length < 6) {
      setSupplierMessage('Zadejte nové heslo (min. 6 znaků).');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/superadmin/suppliers/${supplier._id}/reset-password`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({ newPassword: newPwd })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Chyba změny hesla');
      setSupplierMessage(`Heslo dodavatele "${supplier.companyName}" bylo změněno.`);
      setResetPwdForm(p => ({ ...p, [supplier._id]: '' }));
    } catch (err) {
      setSupplierMessage('Chyba: ' + err.message);
    }
  };
  // ===== End Supplier handlers =====

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#f1f5f9' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
          <div>Načítám konzoli...</div>
        </div>
      </div>
    );
  }

  const s = {
    container: { minHeight: '100vh', background: '#f1f5f9', fontFamily: 'system-ui, sans-serif' },
    header: { background: '#1e293b', color: 'white', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    headerTitle: { margin: 0, fontSize: 20, fontWeight: 700 },
    headerBadge: { background: '#ef4444', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
    logoutBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 14 },
    content: { maxWidth: 1100, margin: '0 auto', padding: 24 },
    card: { background: 'white', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 24, overflow: 'hidden' },
    cardHeader: { padding: '16px 24px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' },
    cardTitle: { margin: 0, fontSize: 16, fontWeight: 600, color: '#1e293b' },
    cardBody: { padding: 24 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
    statCard: { background: 'white', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' },
    statNumber: { fontSize: 36, fontWeight: 700, color: '#3b82f6' },
    statLabel: { color: '#64748b', fontSize: 14, marginTop: 4 },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '10px 16px', borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: 13, fontWeight: 600 },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: 14, verticalAlign: 'middle' },
    tdLast: { padding: '12px 16px', fontSize: 14, verticalAlign: 'middle' },
    badge: { display: 'inline-block', padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 },
    form: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end' },
    formUser: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr auto', gap: 10, alignItems: 'end' },
    input: { padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
    label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 },
    btnPrimary: { background: '#3b82f6', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap' },
    btnSuccess: { background: '#16a34a', color: 'white', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' },
    btnSecondary: { background: 'transparent', color: '#3b82f6', border: '1px solid #3b82f6', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' },
    btnDanger: { background: 'transparent', color: '#ef4444', border: '1px solid #ef4444', padding: '6px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap' },
    btnWarning: { background: '#f59e0b', color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
    dropdownItem: { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 16px', cursor: 'pointer', fontSize: 13, color: '#1e293b', whiteSpace: 'nowrap' },
    select: { padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, outline: 'none', width: '100%' },
    messageBox: { padding: '12px 16px', borderRadius: 8, marginBottom: 16, fontSize: 14, fontWeight: 500 },
    migrationRow: { display: 'flex', gap: 12, alignItems: 'flex-end' },
    userPanel: { background: '#f8fafc', borderTop: '1px solid #e2e8f0', padding: 20 },
    userPanelTitle: { fontSize: 14, fontWeight: 600, color: '#475569', marginBottom: 14 },
    userSmallTable: { width: '100%', borderCollapse: 'collapse', marginBottom: 16 },
    userTh: { textAlign: 'left', padding: '8px 12px', borderBottom: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 12, fontWeight: 600 },
    userTd: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13 },
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.headerTitle}>Super Admin Konzole</h1>
          <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 2 }}>Správa partnerů CRM systému</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={s.headerBadge}>SUPERADMIN</span>
          <button style={s.logoutBtn} onClick={onLogout}>Odhlásit</button>
        </div>
      </div>

      <div style={s.content}>
        {error && <div style={{ ...s.messageBox, background: '#fee2e2', color: '#dc2626' }}>{error}</div>}
        {message && <div style={{ ...s.messageBox, background: message.includes('Chyba') ? '#fee2e2' : '#dcfce7', color: message.includes('Chyba') ? '#dc2626' : '#16a34a' }}>{message}</div>}

        {/* Stats */}
        <div style={s.statsGrid}>
          <div style={s.statCard}>
            <div style={s.statNumber}>{tenants.length}</div>
            <div style={s.statLabel}>Celkem partnerů</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statNumber}>{tenants.reduce((acc, t) => acc + (t.userCount || 0), 0)}</div>
            <div style={s.statLabel}>Uživatelů celkem</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statNumber}>{tenants.reduce((acc, t) => acc + (t.companyCount || 0), 0)}</div>
            <div style={s.statLabel}>Firem celkem</div>
          </div>
          <div style={{ ...s.statCard, background: (migrationStats?.unassignedUsers > 0 || migrationStats?.unassignedCompanies > 0) ? '#fff7ed' : 'white' }}>
            <div style={{ ...s.statNumber, color: (migrationStats?.unassignedUsers > 0) ? '#f59e0b' : '#22c55e' }}>
              {(migrationStats?.unassignedUsers || 0) + (migrationStats?.unassignedCompanies || 0)}
            </div>
            <div style={s.statLabel}>Nepřiřazených záznamů</div>
          </div>
        </div>

        {/* Migration warning */}
        {migrationStats && (migrationStats.unassignedUsers > 0 || migrationStats.unassignedCompanies > 0) && (
          <div style={{ ...s.card, border: '2px solid #f59e0b' }}>
            <div style={{ ...s.cardHeader, background: '#fffbeb' }}>
              <h2 style={{ ...s.cardTitle, color: '#92400e' }}>⚠️ Migrace nepřiřazených dat</h2>
            </div>
            <div style={s.cardBody}>
              <p style={{ color: '#92400e', marginBottom: 16 }}>
                V databázi je <strong>{migrationStats.unassignedUsers} uživatelů</strong> a <strong>{migrationStats.unassignedCompanies} firem</strong>, které nejsou přiřazeny žádnému partnerovi.
                {tenants.length > 0 ? ' Přiřaďte je existujícímu partnerovi (doporučujeme Vyjednáme).' : ' Nejprve vytvořte partnera níže.'}
              </p>
              {migrationStats.unassignedCompanies > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <button
                    style={{ ...s.btnSecondary, fontSize: 13, padding: '6px 14px' }}
                    onClick={handleToggleUnassignedCompanies}
                    disabled={loadingUnassigned}
                  >
                    {loadingUnassigned ? 'Načítám...' : showUnassignedCompanies ? '▲ Skrýt firmy' : `▼ Zobrazit nepřiřazené firmy (${migrationStats.unassignedCompanies})`}
                  </button>
                  {showUnassignedCompanies && (
                    <div style={{ marginTop: 12, border: '1px solid #fcd34d', borderRadius: 8, overflow: 'hidden' }}>
                      <table style={{ ...s.table, fontSize: 13 }}>
                        <thead>
                          <tr style={{ background: '#fffbeb' }}>
                            <th style={{ ...s.th, color: '#92400e' }}>Název firmy</th>
                            <th style={{ ...s.th, color: '#92400e' }}>IČO</th>
                            <th style={{ ...s.th, color: '#92400e' }}>Stav</th>
                            <th style={{ ...s.th, color: '#92400e' }}>Vytvořil</th>
                            <th style={{ ...s.th, color: '#92400e' }}>Datum</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unassignedCompanies.length === 0 ? (
                            <tr><td colSpan={5} style={{ ...s.td, textAlign: 'center', color: '#6b7280' }}>Žádné firmy</td></tr>
                          ) : unassignedCompanies.map(c => (
                            <tr key={c._id}>
                              <td style={s.td}><strong>{c.name || '—'}</strong></td>
                              <td style={s.td}>{c.ico || '—'}</td>
                              <td style={s.td}>{c.status || '—'}</td>
                              <td style={s.td}>{c.createdBy?.name || '—'}</td>
                              <td style={s.td}>{c.createdAt ? new Date(c.createdAt).toLocaleDateString('cs-CZ') : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
              {tenants.length > 0 && (
                <div style={s.migrationRow}>
                  <div style={{ flex: 1 }}>
                    <label style={s.label}>Přiřadit k partnerovi</label>
                    <select style={s.select} value={selectedTenantForMigration} onChange={e => setSelectedTenantForMigration(e.target.value)}>
                      {tenants.map(t => <option key={t._id} value={t._id}>{t.name} ({t.slug})</option>)}
                    </select>
                  </div>
                  <button style={{ ...s.btnWarning, opacity: migrating ? 0.7 : 1 }} onClick={handleMigrate} disabled={migrating}>
                    {migrating ? 'Migruji...' : 'Spustit migraci'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tenant list */}
        <div style={{ ...s.card, overflow: 'visible' }}>
          <div style={{ ...s.cardHeader, borderRadius: '12px 12px 0 0' }}>
            <h2 style={s.cardTitle}>Partneři ({tenants.length})</h2>
          </div>
          {tenants.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Žádní partneři. Přidejte prvního níže.</div>
          ) : (
            tenants.map(tenant => (
              <div key={tenant._id}>
                <table style={{ ...s.table, marginBottom: 0, tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 160 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 160 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 90 }} />
                    <col style={{ width: 80 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 110 }} />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td style={{ ...s.td, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {renamingTenant === tenant._id ? (
                          <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <input
                              autoFocus
                              value={renameValue}
                              onChange={e => setRenameValue(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleRenameTenant(tenant); if (e.key === 'Escape') { setRenamingTenant(null); setRenameValue(''); } }}
                              style={{ border: '1px solid #3b82f6', borderRadius: 6, padding: '4px 8px', fontSize: 14, width: 140 }}
                            />
                            <button onClick={() => handleRenameTenant(tenant)} style={{ ...s.btnSuccess, padding: '4px 10px', fontSize: 12 }}>OK</button>
                            <button onClick={() => { setRenamingTenant(null); setRenameValue(''); }} style={{ ...s.btnSecondary, padding: '4px 8px', fontSize: 12 }}>✕</button>
                          </span>
                        ) : (
                          tenant.name
                        )}
                      </td>
                      <td style={s.td}><code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4 }}>{tenant.slug}</code></td>
                      <td style={s.td}>{tenant.contactEmail || '—'}</td>
                      <td style={s.td}>{tenant.userCount} uživatelů</td>
                      <td style={s.td}>{tenant.companyCount} firem</td>
                      <td style={s.td}>
                        <span style={{ ...s.badge, background: tenant.isActive ? '#dcfce7' : '#f1f5f9', color: tenant.isActive ? '#16a34a' : '#6b7280' }}>
                          {tenant.isActive ? 'Aktivní' : 'Neaktivní'}
                        </span>
                      </td>
                      <td style={s.td}>{new Date(tenant.createdAt).toLocaleDateString('cs-CZ')}</td>
                      <td style={{ ...s.tdLast, position: 'relative' }} ref={openActionMenu === tenant._id ? actionMenuRef : null}>
                        <button
                          style={{ ...s.btnSecondary, padding: '6px 14px', fontWeight: 600, minWidth: 90 }}
                          onClick={() => setOpenActionMenu(openActionMenu === tenant._id ? null : tenant._id)}
                        >
                          Akce ▾
                        </button>
                        {openActionMenu === tenant._id && (
                          <div style={{
                            position: 'absolute', top: '100%', right: 0, zIndex: 200,
                            background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.13)', minWidth: 180, overflow: 'hidden'
                          }}>
                            {/* Uživatelé */}
                            <button
                              style={{ ...s.dropdownItem, background: expandedTenant === tenant._id ? '#eff6ff' : 'white' }}
                              onClick={() => { handleToggleUsers(tenant); setOpenActionMenu(null); }}
                            >
                              👥 {expandedTenant === tenant._id ? 'Skrýt uživatele' : 'Uživatelé'}
                            </button>
                            {/* Sync dat */}
                            <button
                              style={{ ...s.dropdownItem, opacity: syncingTenant === tenant._id ? 0.6 : 1 }}
                              onClick={() => { handleSyncData(tenant); setOpenActionMenu(null); }}
                              disabled={syncingTenant === tenant._id}
                            >
                              🔄 {syncingTenant === tenant._id ? 'Sync...' : 'Sync dat'}
                            </button>
                            {/* Logo */}
                            <label style={{ ...s.dropdownItem, cursor: uploadingLogoFor === tenant._id ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {tenant.logoUrl
                                ? <img src={tenant.logoUrl} alt="logo" style={{ height: 16, width: 'auto', objectFit: 'contain', borderRadius: 2 }} />
                                : '🖼️'}
                              {uploadingLogoFor === tenant._id ? ' Nahrávám...' : ' Logo'}
                              <input
                                type="file"
                                accept="image/*"
                                style={{ display: 'none' }}
                                disabled={uploadingLogoFor === tenant._id}
                                onChange={e => { if (e.target.files[0]) handleLogoUpload(tenant, e.target.files[0]); e.target.value = ''; setOpenActionMenu(null); }}
                              />
                            </label>
                            {/* Přejmenovat */}
                            <button
                              style={s.dropdownItem}
                              onClick={() => { setRenamingTenant(tenant._id); setRenameValue(tenant.name); setOpenActionMenu(null); }}
                            >
                              ✏️ Přejmenovat
                            </button>
                            <div style={{ height: 1, background: '#f1f5f9', margin: '4px 0' }} />
                            {/* Smazat */}
                            <button
                              style={{ ...s.dropdownItem, color: '#dc2626' }}
                              onClick={() => { handleDeleteTenant(tenant); setOpenActionMenu(null); }}
                            >
                              🗑️ Smazat
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Rozbalený panel uživatelů */}
                {expandedTenant === tenant._id && (
                  <div style={s.userPanel}>
                    <div style={s.userPanelTitle}>Uživatelé partnera — {tenant.name}</div>

                    {userMessage && (
                      <div style={{ ...s.messageBox, marginBottom: 12, background: userMessage.includes('Chyba') ? '#fee2e2' : '#dcfce7', color: userMessage.includes('Chyba') ? '#dc2626' : '#16a34a' }}>
                        {userMessage}
                      </div>
                    )}

                    {loadingUsers ? (
                      <div style={{ color: '#94a3b8', marginBottom: 12 }}>Načítám uživatele...</div>
                    ) : (
                      <table style={s.userSmallTable}>
                        <thead>
                          <tr>
                            <th style={s.userTh}>Jméno</th>
                            <th style={s.userTh}>Email</th>
                            <th style={s.userTh}>Uživatelské jméno</th>
                            <th style={s.userTh}>Role</th>
                            <th style={s.userTh}></th>
                          </tr>
                        </thead>
                        <tbody>
                          {(tenantUsers[tenant._id] || []).length === 0 ? (
                            <tr><td colSpan={5} style={{ ...s.userTd, color: '#94a3b8', textAlign: 'center' }}>Žádní uživatelé</td></tr>
                          ) : (
                            (tenantUsers[tenant._id] || []).map(u => (
                              <tr key={u._id}>
                                <td style={s.userTd}>{u.name}</td>
                                <td style={s.userTd}>{u.email || '—'}</td>
                                <td style={s.userTd}>{u.username || '—'}</td>
                                <td style={s.userTd}>
                                  <span style={{ ...s.badge, background: u.role === 'admin' ? '#eff6ff' : '#f1f5f9', color: u.role === 'admin' ? '#2563eb' : '#6b7280' }}>
                                    {u.role}
                                  </span>
                                </td>
                                <td style={{ ...s.userTd, minWidth: 260 }}>
                                  {changingPasswordFor === u._id ? (
                                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                      <input
                                        autoFocus
                                        type="password"
                                        placeholder="Nové heslo"
                                        value={newPasswordValue}
                                        onChange={e => setNewPasswordValue(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') handleChangePassword(u); if (e.key === 'Escape') { setChangingPasswordFor(null); setNewPasswordValue(''); } }}
                                        style={{ border: '1px solid #3b82f6', borderRadius: 6, padding: '4px 8px', fontSize: 13, width: 130 }}
                                      />
                                      <button onClick={() => handleChangePassword(u)} style={{ ...s.btnSuccess, padding: '4px 10px', fontSize: 12 }}>OK</button>
                                      <button onClick={() => { setChangingPasswordFor(null); setNewPasswordValue(''); }} style={{ ...s.btnSecondary, padding: '4px 8px', fontSize: 12 }}>✕</button>
                                    </span>
                                  ) : (
                                    <span style={{ display: 'flex', gap: 6 }}>
                                      <button style={{ ...s.btnSecondary, fontSize: 12, padding: '4px 10px' }} onClick={() => { setChangingPasswordFor(u._id); setNewPasswordValue(''); }}>🔑 Heslo</button>
                                      <button style={{ ...s.btnDanger, fontSize: 12, padding: '4px 10px' }} onClick={() => handleDeleteUser(tenant._id, u)}>Smazat</button>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    )}

                    {/* Formulář nového uživatele */}
                    <div style={{ marginTop: 8, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 10 }}>Přidat admin uživatele</div>
                      <form onSubmit={e => handleCreateUser(e, tenant._id)}>
                        <div style={s.formUser}>
                          <div>
                            <label style={s.label}>Jméno *</label>
                            <input style={s.input} placeholder="Jan Novák" value={userForm.name}
                              onChange={e => setUserForm(p => ({ ...p, name: e.target.value }))} />
                          </div>
                          <div>
                            <label style={s.label}>Email</label>
                            <input style={s.input} type="email" placeholder="jan@partner.cz" value={userForm.email}
                              onChange={e => setUserForm(p => ({ ...p, email: e.target.value }))} />
                          </div>
                          <div>
                            <label style={s.label}>Uživatelské jméno</label>
                            <input style={s.input} placeholder="jnovak" value={userForm.username}
                              onChange={e => setUserForm(p => ({ ...p, username: e.target.value }))} />
                          </div>
                          <div>
                            <label style={s.label}>Heslo *</label>
                            <input style={s.input} type="password" placeholder="••••••••" value={userForm.password}
                              onChange={e => setUserForm(p => ({ ...p, password: e.target.value }))} />
                          </div>
                          <div>
                            <button type="submit" style={{ ...s.btnSuccess, opacity: creatingUser ? 0.7 : 1 }} disabled={creatingUser}>
                              {creatingUser ? 'Vytvářím...' : 'Přidat'}
                            </button>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {/* Create tenant */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>Přidat nového partnera</h2>
          </div>
          <div style={s.cardBody}>
            <form onSubmit={handleCreateTenant}>
              <div style={s.form}>
                <div>
                  <label style={s.label}>Název partnera *</label>
                  <input style={s.input} placeholder="např. Vyjednáme s.r.o." value={newTenant.name}
                    onChange={e => setNewTenant(p => ({ ...p, name: e.target.value }))} />
                </div>
                <div>
                  <label style={s.label}>Slug (unikátní ID) *</label>
                  <input style={s.input} placeholder="např. vyjedname" value={newTenant.slug}
                    onChange={e => setNewTenant(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))} />
                </div>
                <div>
                  <label style={s.label}>Kontaktní email</label>
                  <input style={s.input} type="email" placeholder="kontakt@partner.cz" value={newTenant.contactEmail}
                    onChange={e => setNewTenant(p => ({ ...p, contactEmail: e.target.value }))} />
                </div>
                <div>
                  <button type="submit" style={{ ...s.btnPrimary, opacity: creating ? 0.7 : 1 }} disabled={creating}>
                    {creating ? 'Vytvářím...' : 'Vytvořit partnera'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Change console password */}
        <div style={s.card}>
          <div style={s.cardHeader}>
            <h2 style={s.cardTitle}>🔐 Změnit heslo konzole</h2>
          </div>
          <div style={s.cardBody}>
            {pwdMessage && (
              <div style={{ ...s.messageBox, background: pwdMessage.startsWith('Chyba') ? '#fee2e2' : '#dcfce7', color: pwdMessage.startsWith('Chyba') ? '#dc2626' : '#16a34a', marginBottom: 16 }}>
                {pwdMessage}
              </div>
            )}
            <form onSubmit={handleChangeConsolePassword}>
              <div style={s.form}>
                <div>
                  <label style={s.label}>Současné heslo *</label>
                  <input style={s.input} type="password" placeholder="••••••" value={pwdForm.current}
                    onChange={e => setPwdForm(p => ({ ...p, current: e.target.value }))} />
                </div>
                <div>
                  <label style={s.label}>Nové heslo *</label>
                  <input style={s.input} type="password" placeholder="min. 6 znaků" value={pwdForm.newPwd}
                    onChange={e => setPwdForm(p => ({ ...p, newPwd: e.target.value }))} />
                </div>
                <div>
                  <label style={s.label}>Potvrdit nové heslo *</label>
                  <input style={s.input} type="password" placeholder="zopakujte heslo" value={pwdForm.confirm}
                    onChange={e => setPwdForm(p => ({ ...p, confirm: e.target.value }))} />
                </div>
                <div>
                  <button type="submit" style={s.btnPrimary}>Změnit heslo</button>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* ===== Supplier / Leadtool sekce ===== */}
        <div style={s.card}>
          <div
            style={{ ...s.cardHeader, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => {
              setShowSupplierSection(v => !v);
              if (!showSupplierSection && suppliers.length === 0) fetchSuppliers();
            }}
          >
            <h2 style={s.cardTitle}>Dodavatelé (Leadtool)</h2>
            <span style={{ fontSize: 12, color: '#64748b' }}>{showSupplierSection ? '▲ Skrýt' : '▼ Zobrazit'}</span>
          </div>
          {showSupplierSection && (
            <div style={s.cardBody}>
              {supplierMessage && (
                <div style={{ ...s.messageBox, background: supplierMessage.startsWith('Chyba') ? '#fee2e2' : '#dcfce7', color: supplierMessage.startsWith('Chyba') ? '#dc2626' : '#16a34a', marginBottom: 16 }}>
                  {supplierMessage}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                <button style={s.btnSecondary} onClick={fetchSuppliers} disabled={loadingSuppliers}>
                  {loadingSuppliers ? 'Načítám...' : '↻ Obnovit'}
                </button>
              </div>
              {suppliers.length === 0 && !loadingSuppliers && (
                <p style={{ color: '#64748b' }}>Žádní dodavatelé.</p>
              )}
              {suppliers.map(sup => (
                <div key={sup._id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, padding: 16, marginBottom: 12, background: sup.status === 'pending' ? '#fffbeb' : sup.status === 'approved' ? '#f0fdf4' : '#fef2f2' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>{sup.companyName}</div>
                      <div style={{ fontSize: 13, color: '#64748b' }}>IČO: {sup.ico} | {sup.email}</div>
                      {sup.address && <div style={{ fontSize: 13, color: '#64748b' }}>{sup.address}</div>}
                      {sup.bankAccount && <div style={{ fontSize: 13, color: '#64748b' }}>Účet: {sup.bankAccount}</div>}
                      <div style={{ marginTop: 4 }}>
                        <span style={{ fontSize: 12, padding: '2px 8px', borderRadius: 99, fontWeight: 600,
                          background: sup.status === 'pending' ? '#fef3c7' : sup.status === 'approved' ? '#dcfce7' : '#fee2e2',
                          color: sup.status === 'pending' ? '#b45309' : sup.status === 'approved' ? '#16a34a' : '#dc2626'
                        }}>
                          {sup.status === 'pending' ? 'Čeká na schválení' : sup.status === 'approved' ? 'Schváleno' : 'Zamítnuto'}
                        </span>
                        {sup.batchName && <span style={{ fontSize: 12, marginLeft: 8, color: '#475569' }}>Šarže: <strong>{sup.batchName}</strong></span>}
                        {sup.tenantName && <span style={{ fontSize: 12, marginLeft: 8, color: '#475569' }}>Partner: <strong>{sup.tenantName}</strong></span>}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                      {sup.status !== 'rejected' && (
                        <button style={{ ...s.btnSecondary, fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => handleRejectSupplier(sup)}>
                          Zamítnout
                        </button>
                      )}
                      <button style={{ ...s.btnSecondary, fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }} onClick={() => handleDeleteSupplier(sup)}>
                        Smazat
                      </button>
                    </div>
                  </div>

                  {/* Schválení formulář */}
                  {sup.status === 'pending' && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ ...s.label, marginBottom: 4 }}>Assign partner</label>
                          <select
                            style={{ ...s.input, width: 180 }}
                            value={(approveForm[sup._id] || {}).tenantId || ''}
                            onChange={e => setApproveForm(p => ({ ...p, [sup._id]: { ...(p[sup._id] || {}), tenantId: e.target.value } }))}
                          >
                            <option value=''>-- bez partnera --</option>
                            {tenants.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ ...s.label, marginBottom: 4 }}>Název šarže (batchName) *</label>
                          <input
                            style={{ ...s.input, width: 200 }}
                            type='text'
                            placeholder='dodavatel-12345678'
                            value={(approveForm[sup._id] || {}).batchName || ''}
                            onChange={e => setApproveForm(p => ({ ...p, [sup._id]: { ...(p[sup._id] || {}), batchName: e.target.value } }))}
                          />
                        </div>
                        <button style={{ ...s.btnPrimary, fontSize: 13 }} onClick={() => handleApproveSupplier(sup)}>
                          Schválit
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Update approved supplier */}
                  {sup.status === 'approved' && (
                    <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #e2e8f0' }}>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div>
                          <label style={{ ...s.label, marginBottom: 4 }}>Změnit partnera</label>
                          <select
                            style={{ ...s.input, width: 180 }}
                            value={(approveForm[sup._id] || {}).tenantId || sup.tenantId || ''}
                            onChange={e => setApproveForm(p => ({ ...p, [sup._id]: { ...(p[sup._id] || {}), tenantId: e.target.value } }))}
                          >
                            <option value=''>-- bez partnera --</option>
                            {tenants.map(t => <option key={t._id} value={t._id}>{t.name}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{ ...s.label, marginBottom: 4 }}>Nová šarže</label>
                          <input
                            style={{ ...s.input, width: 200 }}
                            type='text'
                            placeholder={sup.batchName || 'batchName'}
                            value={(approveForm[sup._id] || {}).batchName || ''}
                            onChange={e => setApproveForm(p => ({ ...p, [sup._id]: { ...(p[sup._id] || {}), batchName: e.target.value } }))}
                          />
                        </div>
                        <button style={{ ...s.btnPrimary, fontSize: 13 }} onClick={() => handleApproveSupplier(sup)}>
                          Aktualizovat
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Reset hesla */}
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      style={{ ...s.input, width: 180 }}
                      type='password'
                      placeholder='Nové heslo...'
                      value={resetPwdForm[sup._id] || ''}
                      onChange={e => setResetPwdForm(p => ({ ...p, [sup._id]: e.target.value }))}
                    />
                    <button style={{ ...s.btnSecondary, fontSize: 12 }} onClick={() => handleResetSupplierPassword(sup)}>
                      Reset hesla
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* ===== End Supplier sekce ===== */}

        {/* ===== API Request Log sekce ===== */}
        <div style={s.card}>
          <div
            style={{ ...s.cardHeader, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => {
              setShowRequestLog(v => !v);
              if (!showRequestLog && requestLog === null) fetchRequestLog();
            }}
          >
            <h2 style={s.cardTitle}>📊 API požadavky uživatelů</h2>
            <span style={{ fontSize: 12, color: '#64748b' }}>{showRequestLog ? '▲ Skrýt' : '▼ Zobrazit'}</span>
          </div>
          {showRequestLog && (
            <div style={s.cardBody}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                <button style={s.btnSecondary} onClick={fetchRequestLog} disabled={loadingRequestLog}>
                  {loadingRequestLog ? 'Načítám...' : '↻ Obnovit'}
                </button>
                {requestLog && !requestLog.error && (
                  <span style={{ fontSize: 12, color: '#64748b' }}>
                    Záznamy v paměti (posledních 24h): <strong>{requestLog.total}</strong>
                    {' · '}data se akumulují od restartu serveru
                  </span>
                )}
                {requestLog?.error && (
                  <span style={{ fontSize: 12, color: '#dc2626' }}>Chyba: {requestLog.error}</span>
                )}
              </div>
              {requestLog && requestLog.stats.length === 0 && !requestLog.error && (
                <p style={{ color: '#64748b', fontSize: 14 }}>
                  Žádná data — tracking se aktivuje automaticky při příštím API požadavku od přihlášeného uživatele.
                </p>
              )}
              {requestLog && requestLog.stats.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={s.table}>
                    <thead>
                      <tr style={{ background: '#f1f5f9' }}>
                        <th style={s.th}>Uživatel</th>
                        <th style={s.th}>Email</th>
                        <th style={{ ...s.th, fontFamily: 'monospace', fontSize: 12 }}>IP adresa(y)</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>30 min</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>1 hod</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>6 hod</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>24 hod</th>
                        <th style={s.th}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {requestLog.stats.map((row, i) => {
                        const c30 = row.counts['30m'];
                        const c1h = row.counts['1h'];
                        const color30 = c30 > 200 ? '#dc2626' : c30 > 80 ? '#d97706' : '#16a34a';
                        const color1h = c1h > 400 ? '#dc2626' : c1h > 150 ? '#d97706' : '#16a34a';
                        const isBlocking = blockingUser === row.userId;
                        return (
                          <tr key={row.userId} style={{ background: row.blocked ? '#fff7ed' : i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                            <td style={{ ...s.td, fontWeight: 500 }}>
                              {row.blocked && <span style={{ fontSize: 11, background: '#fef3c7', color: '#b45309', borderRadius: 4, padding: '1px 5px', marginRight: 5, fontWeight: 700 }}>🔒 BAN</span>}
                              {row.name}
                            </td>
                            <td style={{ ...s.td, color: '#64748b' }}>{row.email}</td>
                            <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 11, color: '#475569' }}>{row.ips || '-'}</td>
                            <td style={{ ...s.td, textAlign: 'right', color: color30, fontWeight: 700 }}>{c30}</td>
                            <td style={{ ...s.td, textAlign: 'right', color: color1h, fontWeight: 700 }}>{c1h}</td>
                            <td style={{ ...s.td, textAlign: 'right', color: '#475569' }}>{row.counts['6h']}</td>
                            <td style={{ ...s.td, textAlign: 'right', color: '#94a3b8' }}>{row.counts['24h']}</td>
                            <td style={{ ...s.td, whiteSpace: 'nowrap' }}>
                              {row.blocked ? (
                                <button
                                  style={{ ...s.btnSuccess, fontSize: 11, padding: '4px 10px' }}
                                  disabled={isBlocking}
                                  onClick={() => unblockUser(row.userId)}
                                >
                                  {isBlocking ? '...' : '✓ Odblokovat'}
                                </button>
                              ) : (
                                <div style={{ display: 'flex', gap: 4 }}>
                                  <button
                                    style={{ ...s.btnWarning, fontSize: 11, padding: '4px 10px' }}
                                    disabled={isBlocking}
                                    onClick={() => blockUser(row.userId, 30)}
                                    title="Zablokovat na 30 minut"
                                  >
                                    {isBlocking ? '...' : '⛔ 30 min'}
                                  </button>
                                  <button
                                    style={{ ...s.btnDanger, fontSize: 11, padding: '4px 10px' }}
                                    disabled={isBlocking}
                                    onClick={() => {
                                      if (window.confirm(`Trvale zablokovat uživatele ${row.name}? Lze odblokovat ručně.`)) blockUser(row.userId, 0);
                                    }}
                                    title="Zablokovat na dobu neurčitou"
                                  >
                                    {isBlocking ? '...' : '⛔ ∞'}
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ marginTop: 10, fontSize: 11, color: '#94a3b8' }}>
                    🔴 červená = nadměrné požadavky · 🟡 oranžová = zvýšené · 🟢 zelená = normální
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
        {/* ===== End Request Log sekce ===== */}

      </div>
    </div>
  );
}

export default SuperAdminConsole;

